import { useEffect, useState } from "react";
import clsx from "clsx";

import {
  clearPreview,
  setPreviewColor,
  setQuoteColor,
} from "lib/highlights/use-highlights";
import { useTranslation } from "lib/i18n/use-translation";
import { QUOTE_COLORS } from "lib/quotes/get-quotes";
import { useSelectionMenu } from "lib/selection/use-selection-menu";
import { resolveXpointerRange } from "lib/text/xpointer-range";
import { BottomDockedMenu, FloatingMenu } from "lib/ui/floating-menu";
import { isIOS, useIsTouch } from "lib/utils/device";
import { useIsMobileWidth } from "lib/utils/viewport";

import BookmarkFilledIcon from "assets/icons/bookmark-filled.svg?react";
import BookmarkIcon from "assets/icons/bookmark.svg?react";
import CheckIcon from "assets/icons/check.svg?react";
import CopyIcon from "assets/icons/copy.svg?react";
import EditIcon from "assets/icons/edit.svg?react";
import QuoteColorsIcon from "assets/icons/quote-colors.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import TrashIcon from "assets/icons/trash.svg?react";
import css from "./selection-menu.module.scss";

type QuoteColorPreviewProps = {
  quoteId: string;
  color: string;
  originalColor: string;
  confirmedRef: { current: boolean };
};

function QuoteColorPreview({
  quoteId,
  color,
  originalColor,
  confirmedRef,
}: QuoteColorPreviewProps) {
  useEffect(() => {
    setQuoteColor(quoteId, color);
    return () => {
      if (confirmedRef.current) {
        confirmedRef.current = false;
      } else {
        setQuoteColor(quoteId, originalColor);
      }
    };
  }, [quoteId, color, originalColor, confirmedRef]);
  return null;
}

type SelectionPreviewProps = {
  startXpointer: string;
  endXpointer: string;
  color: string;
};

function SelectionPreview({
  startXpointer,
  endXpointer,
  color,
}: SelectionPreviewProps) {
  useEffect(() => {
    const range = resolveXpointerRange(startXpointer, endXpointer);
    if (range) {
      setPreviewColor(range, color);
    }
    return () => clearPreview();
  }, [startXpointer, endXpointer, color]);
  return null;
}

export function SelectionMenu() {
  const { t } = useTranslation();
  const {
    isOpen,
    mode,
    anchor,
    menuRef,
    selectedColor,
    isBookmarked,
    isPageBookmarked,
    onCreateQuote,
    onUpdateQuoteColor,
    onDeleteQuote,
    onBookmark,
    onPageBookmark,
    onCopy,
    onAddNote,
    onEditNote,
    onShare,
    selectColor,
    previewQuote,
    previewXpointers,
    colorConfirmedRef,
  } = useSelectionMenu();

  const [showSwatches, setShowSwatches] = useState(false);

  const touch = useIsTouch();
  const isMobileWidth = useIsMobileWidth();
  const isTablet = touch && !isMobileWidth;
  const isPhone = touch && !isTablet;

  if (!isOpen) {
    if (showSwatches) setShowSwatches(false);
    return null;
  }

  const isQuoteMode = mode === "createQuote" || mode === "updateQuote";

  const selectionMenuContent = (
    <div className={css.menu}>
      {previewQuote && (
        <QuoteColorPreview
          quoteId={previewQuote.id}
          color={selectedColor}
          originalColor={previewQuote.originalColor}
          confirmedRef={colorConfirmedRef}
        />
      )}
      {previewXpointers && (
        <SelectionPreview
          startXpointer={previewXpointers.start}
          endXpointer={previewXpointers.end}
          color={selectedColor}
        />
      )}
      {isQuoteMode && (
        <>
          <div className={css.swatchRow}>
            <div className={css.swatchList}>
              {QUOTE_COLORS.map(color => (
                <button
                  key={color}
                  className={clsx(css.swatch, `quote-${color}`)}
                  onClick={() => selectColor(color)}
                >
                  {selectedColor === color && (
                    <CheckIcon width={12} height={12} />
                  )}
                </button>
              ))}
            </div>
            <button
              className={css.confirmButton}
              onClick={() =>
                mode === "createQuote"
                  ? onCreateQuote(selectedColor)
                  : onUpdateQuoteColor(selectedColor)
              }
            >
              <CheckIcon />
            </button>
          </div>

          {showSwatches ? (
            <>
              <div className={css.dockedSwatches}>
                {QUOTE_COLORS.map(color => (
                  <button
                    key={color}
                    className={clsx(css.swatch, `quote-${color}`)}
                    onClick={() => selectColor(color)}
                  >
                    {selectedColor === color && (
                      <CheckIcon width={10} height={10} />
                    )}
                  </button>
                ))}
              </div>
              <button
                className={css.confirmButton}
                onClick={() =>
                  mode === "createQuote"
                    ? onCreateQuote(selectedColor)
                    : onUpdateQuoteColor(selectedColor)
                }
              >
                <CheckIcon />
              </button>
            </>
          ) : (
            <>
              <button
                className={clsx(css.iconButton, css.dockedOnly)}
                onClick={() => setShowSwatches(true)}
              >
                <QuoteColorsIcon />
              </button>

              <button
                className={css.iconButton}
                onClick={mode === "createQuote" ? onAddNote : onEditNote}
              >
                <EditIcon />
                <span>
                  {mode === "createQuote"
                    ? t("Добавить заметку")
                    : t("Редактировать заметку")}
                </span>
              </button>

              <button
                className={clsx(css.iconButton, {
                  [css.active]: isBookmarked,
                })}
                onClick={onBookmark}
              >
                {isBookmarked ? <BookmarkFilledIcon /> : <BookmarkIcon />}
                <span>
                  {isBookmarked
                    ? t("Удалить закладку")
                    : t("Поставить закладку")}
                </span>
              </button>

              <button className={css.iconButton} onClick={onCopy}>
                <CopyIcon />
                <span>{t("Скопировать")}</span>
              </button>

              <button className={css.iconButton} onClick={onShare}>
                <ShareIcon />
                <span>{t("Поделиться")}</span>
              </button>

              {mode === "updateQuote" && (
                <button
                  className={clsx(css.iconButton, css.danger)}
                  onClick={onDeleteQuote}
                >
                  <TrashIcon />
                  <span>{t("Удалить")}</span>
                </button>
              )}
            </>
          )}
        </>
      )}

      {(mode === "bookmarkParagraph" || mode === "bookmarkPage") && (
        <button
          className={clsx(css.iconButton, {
            [css.active]: isPageBookmarked,
          })}
          onClick={onPageBookmark}
        >
          {isPageBookmarked ? <BookmarkFilledIcon /> : <BookmarkIcon />}
          <span>
            {isPageBookmarked ? t("Удалить закладку") : t("Поставить закладку")}
          </span>
        </button>
      )}
    </div>
  );

  if (isPhone && isIOS()) {
    return (
      <BottomDockedMenu ref={menuRef} className={css.dockedContainer}>
        {selectionMenuContent}
      </BottomDockedMenu>
    );
  }

  const isAndroidPhone = isPhone && !isIOS();

  return (
    <FloatingMenu
      ref={menuRef}
      anchor={anchor}
      offset={15}
      preferOppositeSide={isTablet || isAndroidPhone}
      dockedClassName={css.dockedContainer}
      className={clsx(isAndroidPhone && css.dockedContainer)}
    >
      {selectionMenuContent}
    </FloatingMenu>
  );
}
